const DEFAULT_CATALOG_INDEX = "ich-price-list-base";
const DEFAULT_CATALOG_ID = "ich-structural";
const CATALOG_REF_SOURCE = "sku-photo-mapper";
const MAX_GENERATED_TAG_LENGTH = 512;

function cleanText(value) {
	if (value === null || value === undefined) {
		return "";
	}
	return `${value}`.trim();
}

function firstText(...values) {
	for (let index = 0; index < values.length; index += 1) {
		const value = cleanText(values[index]);
		if (value) {
			return value;
		}
	}
	return "";
}

export function mergeUniqueCatalogValues(values = []) {
	const seen = new Set();
	const result = [];
	const sourceValues = Array.isArray(values) ? values : [values];
	sourceValues.forEach((value) => {
		const normalized = cleanText(value);
		if (!normalized) {
			return;
		}
		const key = normalized.toLowerCase();
		if (seen.has(key)) {
			return;
		}
		seen.add(key);
		result.push(normalized);
	});
	return result;
}

function mergeUniqueGeneratedTags(values = []) {
	const sourceValues = Array.isArray(values) ? values : [values];
	return mergeUniqueCatalogValues(
		sourceValues.map((value) => cleanText(value).replace(/\s+/g, " ").slice(0, MAX_GENERATED_TAG_LENGTH))
	);
}

function getCategoryValues(source = {}, metadata = {}) {
	return mergeUniqueGeneratedTags([
		...(Array.isArray(source.categories) ? source.categories : []),
		...(Array.isArray(metadata.categories) ? metadata.categories : []),
		source.category,
		source.Category,
		metadata.category,
		metadata.Category
	]);
}

function getCatalogTargetSource(target = {}) {
	const raw = target && target.raw && typeof target.raw === "object" ? target.raw : {};
	const metadata = raw.metadata && typeof raw.metadata === "object"
		? raw.metadata
		: (target.metadata && typeof target.metadata === "object" ? target.metadata : {});
	return {
		...raw,
		...target,
		metadata
	};
}

function getCatalogRefKey(ref = {}) {
	const catalog = firstText(ref.catalog, ref.catalogId, DEFAULT_CATALOG_ID).toLowerCase();
	const sku = firstText(ref.sku, ref.SKU, ref.catalogRefId).toLowerCase();
	return catalog && sku ? `${catalog}::${sku}` : "";
}

function normalizeStoredCatalogRef(ref = {}) {
	if (typeof ref === "string") {
		const sku = cleanText(ref);
		return sku ? {
			catalog: DEFAULT_CATALOG_ID,
			catalogVersion: DEFAULT_CATALOG_INDEX,
			indexName: DEFAULT_CATALOG_INDEX,
			sku,
			objectIDs: [],
			skuGroupIds: [],
			generatedTags: [],
			source: CATALOG_REF_SOURCE,
			state: "approved",
			scope: "global",
			role: "gallery"
		} : null;
	}
	if (!ref || typeof ref !== "object") {
		return null;
	}
	const sku = firstText(ref.sku, ref.SKU, ref.catalogRefId);
	if (!sku) {
		return null;
	}
	return {
		...ref,
		catalog: firstText(ref.catalog, ref.catalogId, DEFAULT_CATALOG_ID),
		catalogVersion: firstText(
			ref.catalogVersion,
			ref.pricingSnapshotId,
			ref.indexName,
			ref.index,
			DEFAULT_CATALOG_INDEX
		),
		indexName: firstText(ref.indexName, ref.index, DEFAULT_CATALOG_INDEX),
		sku,
		objectIDs: mergeUniqueCatalogValues([
			...(Array.isArray(ref.objectIDs) ? ref.objectIDs : []),
			ref.objectID
		]),
		skuGroupIds: mergeUniqueCatalogValues([
			...(Array.isArray(ref.skuGroupIds) ? ref.skuGroupIds : []),
			ref.skuGroupId
		]),
		generatedTags: mergeUniqueGeneratedTags(ref.generatedTags || ref.tagValues || []),
		source: firstText(ref.source, CATALOG_REF_SOURCE),
		state: firstText(ref.state, "approved"),
		scope: firstText(ref.scope, "global"),
		role: firstText(ref.role, "gallery")
	};
}

export function normalizeSkuPhotoMappingTarget(target = {}, options = {}) {
	const source = getCatalogTargetSource(target);
	const metadata = source.metadata || {};
	const objectID = firstText(source.objectID, source.id, metadata.objectID, metadata.id);
	const sku = firstText(source.sku, source.SKU, metadata.sku, metadata.SKU);
	if (!sku) {
		return null;
	}
	const desc = firstText(
		source.desc,
		source["Option Name"],
		source.description,
		source.label,
		source.name,
		metadata.desc,
		metadata.description
	);
	const subtitle = firstText(source.subtitle, source.Subtitle, metadata.subtitle, metadata.Subtitle);
	const notes = firstText(source.notes, source.Notes, source.note, metadata.notes, metadata.Notes);
	const categories = getCategoryValues(source, metadata);
	const skuGroupId = firstText(
		source.skuGroupId,
		source["SKU Group ID"],
		source.groupId,
		metadata.skuGroupId,
		metadata["SKU Group ID"],
		metadata.groupId
	);
	const indexName = firstText(
		source.indexName,
		source.catalogIndex,
		options.indexName,
		DEFAULT_CATALOG_INDEX
	);
	const catalog = firstText(source.catalog, source.catalogId, options.catalog, DEFAULT_CATALOG_ID);
	const catalogVersion = firstText(
		source.catalogVersion,
		source.pricingSnapshotId,
		options.catalogVersion,
		options.pricingSnapshotId,
		indexName
	);
	return {
		catalog,
		catalogVersion,
		indexName,
		sku,
		objectIDs: mergeUniqueCatalogValues([objectID]),
		skuGroupIds: mergeUniqueCatalogValues([skuGroupId]),
		generatedTags: mergeUniqueGeneratedTags([sku, desc, subtitle, notes, ...categories]),
		display: {
			desc,
			subtitle,
			notes,
			categories
		},
		source: CATALOG_REF_SOURCE,
		state: "approved",
		scope: "global",
		role: "gallery"
	};
}

export function normalizeSkuPhotoMappingTargets(targets = [], options = {}) {
	const refsByKey = {};
	(targets || []).forEach((target) => {
		const normalized = normalizeSkuPhotoMappingTarget(target, options);
		const key = normalized ? getCatalogRefKey(normalized) : "";
		if (!key) {
			return;
		}
		const existing = refsByKey[key];
		if (!existing) {
			refsByKey[key] = normalized;
			return;
		}
		refsByKey[key] = {
			...existing,
			catalog: normalized.catalog,
			catalogVersion: normalized.catalogVersion,
			indexName: normalized.indexName,
			objectIDs: mergeUniqueCatalogValues([...existing.objectIDs, ...normalized.objectIDs]),
			skuGroupIds: mergeUniqueCatalogValues([...existing.skuGroupIds, ...normalized.skuGroupIds]),
			generatedTags: mergeUniqueGeneratedTags([...existing.generatedTags, ...normalized.generatedTags]),
			display: {
				desc: firstText(existing.display.desc, normalized.display.desc),
				subtitle: firstText(existing.display.subtitle, normalized.display.subtitle),
				notes: firstText(existing.display.notes, normalized.display.notes),
				categories: mergeUniqueGeneratedTags([
					...(existing.display.categories || []),
					...(normalized.display.categories || [])
				])
			}
		};
	});
	return Object.keys(refsByKey).map((key) => refsByKey[key]);
}

function normalizeExistingCatalogRefs(metaData = {}) {
	const refsByKey = {};
	const refs = Array.isArray(metaData.catalogRefs) ? metaData.catalogRefs : [];
	refs.forEach((ref) => {
		const normalized = normalizeStoredCatalogRef(ref);
		const key = normalized ? getCatalogRefKey(normalized) : "";
		if (!key) {
			return;
		}
		const existing = refsByKey[key];
		refsByKey[key] = existing ? {
			...existing,
			...normalized,
			objectIDs: mergeUniqueCatalogValues([...existing.objectIDs, ...normalized.objectIDs]),
			skuGroupIds: mergeUniqueCatalogValues([...existing.skuGroupIds, ...normalized.skuGroupIds]),
			generatedTags: mergeUniqueGeneratedTags([...existing.generatedTags, ...normalized.generatedTags])
		} : normalized;
	});
	return refsByKey;
}

function applyCatalogRefProjections(metaData, refs) {
	const nextMetaData = { ...metaData };
	if (refs.length === 0) {
		delete nextMetaData.catalogRefs;
		delete nextMetaData.catalogRefIds;
		delete nextMetaData.catalogObjectIds;
		return nextMetaData;
	}
	nextMetaData.catalogRefs = refs;
	nextMetaData.catalogRefIds = mergeUniqueCatalogValues(refs.map((ref) => ref.sku));
	nextMetaData.catalogObjectIds = mergeUniqueCatalogValues(
		refs.reduce((ids, ref) => ids.concat(ref.objectIDs || []), [])
	);
	return nextMetaData;
}

export function linkSkuPhotoMappings(metaData = {}, targets = [], options = {}) {
	const nextTargets = normalizeSkuPhotoMappingTargets(targets, options);
	if (nextTargets.length === 0) {
		return { ...(metaData || {}) };
	}
	const refsByKey = normalizeExistingCatalogRefs(metaData);
		nextTargets.forEach((target) => {
		const key = getCatalogRefKey(target);
		const existing = refsByKey[key];
		refsByKey[key] = existing ? {
			...existing,
			catalog: target.catalog,
			catalogVersion: target.catalogVersion,
			indexName: target.indexName,
			objectIDs: mergeUniqueCatalogValues([...existing.objectIDs, ...target.objectIDs]),
			skuGroupIds: mergeUniqueCatalogValues([...existing.skuGroupIds, ...target.skuGroupIds]),
			generatedTags: mergeUniqueGeneratedTags([...existing.generatedTags, ...target.generatedTags]),
			source: CATALOG_REF_SOURCE,
			state: "approved",
			scope: "global",
			role: "gallery"
		} : {
			catalog: target.catalog,
			catalogVersion: target.catalogVersion,
			indexName: target.indexName,
			sku: target.sku,
			objectIDs: target.objectIDs,
			skuGroupIds: target.skuGroupIds,
			generatedTags: target.generatedTags,
			source: CATALOG_REF_SOURCE,
			state: "approved",
			scope: "global",
			role: "gallery"
		};
	});

	const existingTags = mergeUniqueCatalogValues(metaData.tags || []);
	const previouslyManagedTags = mergeUniqueCatalogValues(metaData.catalogGeneratedTags || []);
	const existingTagKeys = new Set(existingTags.map((tag) => tag.toLowerCase()));
	const previouslyManagedKeys = new Set(previouslyManagedTags.map((tag) => tag.toLowerCase()));
	const requestedTags = mergeUniqueGeneratedTags(
		nextTargets.reduce((tags, target) => tags.concat(target.generatedTags || []), [])
	);
	const newlyManagedTags = requestedTags.filter((tag) => (
		!existingTagKeys.has(tag.toLowerCase()) || previouslyManagedKeys.has(tag.toLowerCase())
	));
	let nextMetaData = applyCatalogRefProjections(metaData, Object.keys(refsByKey).map((key) => refsByKey[key]));
	nextMetaData.tags = mergeUniqueCatalogValues([...existingTags, ...requestedTags]);
	nextMetaData.catalogGeneratedTags = mergeUniqueCatalogValues([...previouslyManagedTags, ...newlyManagedTags]);
	return nextMetaData;
}

export function unlinkSkuPhotoMappings(metaData = {}, targets = [], options = {}) {
	const refsByKey = normalizeExistingCatalogRefs(metaData);
	const targetKeys = new Set(
		normalizeSkuPhotoMappingTargets(targets, options)
			.map((target) => getCatalogRefKey(target))
			.filter(Boolean)
	);
	if (targetKeys.size === 0) {
		return { ...(metaData || {}) };
	}
	targetKeys.forEach((key) => delete refsByKey[key]);
	const remainingRefs = Object.keys(refsByKey).map((key) => refsByKey[key]);
	const remainingRequiredTagKeys = new Set(
		mergeUniqueCatalogValues(
			remainingRefs.reduce((tags, ref) => tags.concat(ref.generatedTags || []), [])
		).map((tag) => tag.toLowerCase())
	);
	const managedTags = mergeUniqueCatalogValues(metaData.catalogGeneratedTags || []);
	const removableTagKeys = new Set(
		managedTags
			.filter((tag) => !remainingRequiredTagKeys.has(tag.toLowerCase()))
			.map((tag) => tag.toLowerCase())
	);
	let nextMetaData = applyCatalogRefProjections(metaData, remainingRefs);
	nextMetaData.tags = mergeUniqueCatalogValues(metaData.tags || [])
		.filter((tag) => !removableTagKeys.has(tag.toLowerCase()));
	const nextManagedTags = managedTags.filter((tag) => remainingRequiredTagKeys.has(tag.toLowerCase()));
	if (nextManagedTags.length > 0) {
		nextMetaData.catalogGeneratedTags = nextManagedTags;
	} else {
		delete nextMetaData.catalogGeneratedTags;
	}
	return nextMetaData;
}

export function getSkuPhotoMappingSummary(targets = [], options = {}) {
	const normalized = normalizeSkuPhotoMappingTargets(targets, options);
	return {
		skuCount: normalized.length,
		variantCount: normalized.reduce((count, target) => count + target.objectIDs.length, 0),
		skus: normalized.map((target) => target.sku),
		searchText: normalized.length > 0
			? firstText(normalized[0].display.desc, normalized[0].display.subtitle, normalized[0].sku)
			: ""
	};
}

export function getSkuPhotoMappingActionPresentation(summary = {}, selectedPhotoCount = 0, skuDisplayLimit = 4) {
	const photoCount = Math.max(0, Number(selectedPhotoCount) || 0);
	const skus = Array.isArray(summary.skus) ? summary.skus : [];
	const skuCount = Math.max(0, Number(summary.skuCount) || skus.length);
	const catalogOptionCount = Math.max(0, Number(summary.variantCount) || 0);
	const displayLimit = Math.max(1, Number(skuDisplayLimit) || 4);
	const displayedSkus = skus.slice(0, displayLimit);
	const hiddenSkuCount = Math.max(0, skuCount - displayedSkus.length);
	const photoLabel = photoCount === 1 ? "photo" : "photos";
	const skuLabel = skuCount === 1 ? "SKU" : "SKUs";
	const catalogOptionLabel = catalogOptionCount === 1 ? "catalog option" : "catalog options";
	const hasMappingTargets = skuCount > 0;
	const canMutate = hasMappingTargets && photoCount > 0;
	let instruction = "Select one or more target SKUs in the activity.";
	if (hasMappingTargets && photoCount === 0) {
		instruction = `Select one or more Drive photos to map to ${skuCount} ${skuLabel}.`;
	} else if (canMutate) {
		instruction = "Each selected photo will be updated with every target SKU.";
	}

	return {
		photoCount,
		photoLabel,
		skuCount,
		skuLabel,
		catalogOptionCount,
		catalogOptionLabel,
		displayedSkus,
		hiddenSkuCount,
		hasMappingTargets,
		canMutate,
		instruction,
		selectedSkuTitle: hasMappingTargets ? `Target SKUs: ${skus.join(", ")}` : "No target SKUs selected",
		linkLabel: `Link ${photoCount} ${photoLabel} to ${skuCount} ${skuLabel}`,
		unlinkLabel: `Unlink ${photoCount} ${photoLabel} from ${skuCount} ${skuLabel}`
	};
}

export function getSkuPhotoMappingSelectionStatusUpdate(lastResult = null, selectedPhotoCount = 0) {
	const selectedAssetCount = Math.max(0, Number(selectedPhotoCount) || 0);
	const preservedResult = lastResult && lastResult.status === "error"
		? lastResult
		: null;
	return preservedResult ? {
		...preservedResult,
		selectedAssetCount
	} : {
		status: "ready",
		message: "Drive photo mapper is ready.",
		selectedAssetCount
	};
}

export { DEFAULT_CATALOG_ID, DEFAULT_CATALOG_INDEX };
